import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
@Component({
    selector: 'app-signature-pad',
    standalone: true,
    templateUrl: './signature-pad.component.html',
    styleUrls: ['./signature-pad.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignaturePadComponent implements OnInit, AfterViewInit {
    @Output() signatureDrawn = new EventEmitter<string>();
    private canvas: HTMLCanvasElement
    private ongoingTouches = [];
    private context;
    private isDrawing = false;
    private x = 0;
    private y = 0;
    private offsetX;
    private offsetY;
    private strokeColor: '#000000'
    constructor(private cdr: ChangeDetectorRef) { }

    ngOnInit(): void {
        this.canvas = document.getElementById('canvas') as HTMLCanvasElement;
        this.context = this.canvas.getContext('2d');
        this.startup();
    }
    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }
    startup() {
        this.handleStart = this.handleStart.bind(this);
        this.handleEnd = this.handleEnd.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.handleMove = this.handleMove.bind(this);
    
        const getOffset = (e: any) => {
            const rect = this.canvas.getBoundingClientRect();
            const x = e.clientX || e.touches?.[0]?.clientX;
            const y = e.clientY || e.touches?.[0]?.clientY;
            return {
                x: x - rect.left,
                y: y - rect.top
            };
        };
    
        // Event listeners for both touch and mouse
        this.canvas.addEventListener('touchstart', (e) => {
            const { x, y } = getOffset(e);
            this.x = x;
            this.y = y;
            this.isDrawing = true;
        });
    
        this.canvas.addEventListener('touchmove', (e) => {
            if (this.isDrawing) {
                const { x, y } = getOffset(e);
                this.drawLine(this.context, this.x, this.y, x, y);
                this.x = x;
                this.y = y;
            }
        });
    
        this.canvas.addEventListener('touchend', (e) => {
            if (this.isDrawing) {
                const { x, y } = getOffset(e);
                this.drawLine(this.context, this.x, this.y, x, y);
                this.x = 0;
                this.y = 0;
                this.isDrawing = false;
            }
    
            if (!this.isCanvasBlank(this.canvas)) {
                const croppedImage = this.removeImageSpace(this.canvas);
                this.signatureDrawn.emit(croppedImage.toDataURL());
            }
        });
    
        // Mouse events
        this.canvas.addEventListener('mousedown', (e) => {
            const { x, y } = getOffset(e);
            this.x = x;
            this.y = y;
            this.isDrawing = true;
        });
    
        this.canvas.addEventListener('mousemove', (e) => {
            if (this.isDrawing) {
                const { x, y } = getOffset(e);
                this.drawLine(this.context, this.x, this.y, x, y);
                this.x = x;
                this.y = y;
            }
        });
    
        this.canvas.addEventListener('mouseup', (e) => {
            if (this.isDrawing) {
                const { x, y } = getOffset(e);
                this.drawLine(this.context, this.x, this.y, x, y);
                this.x = 0;
                this.y = 0;
                this.isDrawing = false;
            }
    
            if (!this.isCanvasBlank(this.canvas)) {
                const croppedImage = this.removeImageSpace(this.canvas);
                this.signatureDrawn.emit(croppedImage.toDataURL());
            }
        });
    }
    
    handleStart(evt) {
        evt.preventDefault();
        const touches = evt.changedTouches;
        this.offsetX = this.canvas.getBoundingClientRect().left;
        this.offsetY = this.canvas.getBoundingClientRect().top;
        for (let i = 0; i < touches.length; i++) {
            this.ongoingTouches.push(this.copyTouch(touches[i]));
        }
    }

    handleMove(evt) {
        evt.preventDefault();
        const touches = evt.changedTouches;
        for (let i = 0; i < touches.length; i++) {
            const idx = this.ongoingTouchIndexById(touches[i].identifier);
            if (idx >= 0) {
                this.context.beginPath();
                this.context.moveTo(this.ongoingTouches[idx].clientX - this.offsetX, this.ongoingTouches[idx].clientY - this.offsetY);
                this.context.lineTo(touches[i].clientX - this.offsetX, touches[i].clientY - this.offsetY);
                this.context.lineWidth = 2;
                this.context.strokeStyle = this.strokeColor;
                this.context.lineJoin = "round";
                this.context.closePath();
                this.context.stroke();
                this.ongoingTouches.splice(idx, 1, this.copyTouch(touches[i]));  // swap in the new touch record
            }
        }
    }

    handleEnd(evt) {
        evt.preventDefault();
        const touches = evt.changedTouches;
        for (let i = 0; i < touches.length; i++) {
            let idx = this.ongoingTouchIndexById(touches[i].identifier);
            if (idx >= 0) {
                this.context.lineWidth = 2
                this.context.fillStyle = this.strokeColor;
                this.ongoingTouches.splice(idx, 1);
            }
        }
    }

    isCanvasBlank(canvas) {
        const context = canvas.getContext('2d');

        const pixelBuffer = new Uint32Array(
            context.getImageData(0, 0, canvas.width, canvas.height).data.buffer
        );

        return !pixelBuffer.some(color => color !== 0);
    }
    handleCancel(evt) {
        evt.preventDefault();
        const touches = evt.changedTouches;
        for (let i = 0; i < touches.length; i++) {
            let idx = this.ongoingTouchIndexById(touches[i].identifier);
            this.ongoingTouches.splice(idx, 1);
        }
    }

    copyTouch({ identifier, clientX, clientY }) {
        return { identifier, clientX, clientY };
    }

    ongoingTouchIndexById(idToFind) {
        for (let i = 0; i < this.ongoingTouches.length; i++) {
            const id = this.ongoingTouches[i].identifier;
            if (id === idToFind) {
                return i;
            }
        }
        return -1;    // not found
    }

    drawLine(context, x1, y1, x2, y2) {
        context.beginPath();
        context.lineJoin = "round";
        context.moveTo(x1, y1);
        context.lineTo(x2, y2);
        context.closePath();
        context.stroke();
    }

    clearArea() {
        this.context.setTransform(1, 0, 0, 1, 0, 0);
        this.context.clearRect(0, 0, this.context.canvas.width, this.context.canvas.height);
        this.signatureDrawn.emit(null);
    }
    removeImageSpace(canvas){
        let tempContext = canvas.getContext('2d');
   let copyCanvas = document.createElement('canvas').getContext('2d');
   let pixels = tempContext.getImageData(0, 0, canvas.width, canvas.height);
    let l = pixels.data.length;
    let bound = {
      top: null,
      left: null,
      right: null,
      bottom: null
    },
    x, y;

  for (let i = 0; i < l; i += 4) {
    if (pixels.data[i+3] !== 0) {
      x = (i / 4) % canvas.width;
      y = ~~((i / 4) / canvas.width);
  
      if (bound.top === null) {
        bound.top = y;
      }
      
      if (bound.left === null) {
        bound.left = x; 
      } else if (x < bound.left) {
        bound.left = x;
      }
      
      if (bound.right === null) {
        bound.right = x; 
      } else if (bound.right < x) {
        bound.right = x;
      }
      
      if (bound.bottom === null) {
        bound.bottom = y;
      } else if (bound.bottom < y) {
        bound.bottom = y;
      }
    }
  }
    
  var trimHeight = bound.bottom - bound.top + 1,
      trimWidth = bound.right - bound.left + 1,
      trimmed = tempContext.getImageData(bound.left, bound.top, trimWidth, trimHeight);
  
  copyCanvas.canvas.width = trimWidth;
  copyCanvas.canvas.height = trimHeight;
  copyCanvas.putImageData(trimmed, 0, 0);
  
  return copyCanvas.canvas;
    }
}